/**
 * Audit Template Type Definition
 *
 * Defines the structure for CyberSweep audit templates that control
 * due date calculations and phase requirements.
 */

export interface AuditTemplate {
  /** Unique identifier for the template */
  id: string;
  /** Display name of the template */
  name: string;
  /** Days from audit start date to Phase 1 due date */
  phase1DueDateDays: number;
  /** Days after Phase 1 complete to Phase 2 due date (0 = no Phase 2) */
  phase2DueDateDays: number;
  /** Whether Phase 2 is required - derived from phase2DueDateDays > 0 */
  phase2Required: boolean;
  /** Type of website checklist to use - initial_deletion (101 sites), comprehensive (237 sites), or minimal (77 sites) */
  checklistType: 'initial_deletion' | 'comprehensive' | 'minimal';
}

/**
 * Calculate due dates based on a start date and audit template
 */
export const calculateDueDates = (
  startDate: Date,
  template: AuditTemplate
): { phase1Due: Date; phase2Due: Date | null; phase2Required: boolean } => {
  const phase1Due = new Date(startDate);
  phase1Due.setDate(phase1Due.getDate() + template.phase1DueDateDays);

  const phase2Due = template.phase2Required
    ? new Date(phase1Due.getTime() + template.phase2DueDateDays * 24 * 60 * 60 * 1000)
    : null;

  return { phase1Due, phase2Due, phase2Required: template.phase2Required };
};

/**
 * Format a date to YYYY-MM-DD string for form inputs
 */
export const formatDateForInput = (date: Date): string => {
  return date.toISOString().split('T')[0];
};
